import React, { FC, ReactElement, useEffect } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Token from '@/utils/token';
import {
  selectAccessManifest,
  selectAuthToken,
  selectUserPermissions,
  setAccess,
  setTokens,
} from '@/store/auth';
import { useGetAccessQuery } from '@/services/base.api';
import {
  useLegalDocumentsMutation,
  legalContentLang,
} from '@/services/legal.api';
import { selectLegalFetchAttemptedFor, LegalState } from '@/store/legal';
import { REDIRECT_SEARCH_PARAM_KEY } from '@/constants/system';
import { getRedirectQueryStringInCurrentLocation } from '@/utils/misc.utils';
import i18next from 'i18next';

type AuthedContainerProps = {
  children: ReactElement;
  requiredPermissions?: string[];
};

// Narrowest shape the legal selector needs off the store. `useStore()` is typed
// loosely, so name the slice explicitly rather than casting to `any`.
type LegalOwnerState = { legal: LegalState };

const AuthedContainer: FC<AuthedContainerProps> = ({
  children,
  requiredPermissions,
}: AuthedContainerProps) => {
  const tokens = useSelector(selectAuthToken);
  const permissions = useSelector(selectUserPermissions);
  const accessManifest = useSelector(selectAccessManifest);
  const dispatch = useDispatch();

  let access: string;
  let refresh = '';

  if (!tokens.access) {
    access = Token.getAccessToken() ?? '';
    refresh = Token.getRefreshToken() ?? '';
  } else {
    access = tokens.access;
  }

  // Rehydrate the persisted token into the store on first mount.
  useEffect(() => {
    if (access) {
      dispatch(
        setTokens({
          access,
          refresh,
        })
      );
    }
  }, []);

  // Batch 001: permissions/roles no longer ride in the lean JWT. Fetch the
  // capability manifest from GET /v1/access once the token is in the store
  // (authedQuery reads the Bearer from there) and mirror it into the auth slice
  // so every guard/selector reads a single source.
  const { data: accessData } = useGetAccessQuery(undefined, {
    skip: !tokens.access,
  });
  useEffect(() => {
    if (accessData) {
      dispatch(setAccess(accessData));
    }
  }, [accessData]);

  // Batch 004 / ADR-5: the legal-document list normally arrives with the runtime
  // config, before the first render (index.tsx bootstrapLegalDocuments). That
  // covers a reload while logged in. It does NOT cover logging in inside an
  // already-loaded page: advanceLoginStep lands with navigate('/'), an SPA
  // navigation, so bootstrapConfig never runs again and the list would stay empty
  // for the whole session — the sidebar's Terms/Privacy links would never appear at
  // all. So trigger the same fetch here, the repo's authenticated per-session
  // bootstrap site, right next to GET /v1/access.
  //
  // Guarded on the recorded attempt, NOT on "the list is empty": an empty list is
  // the correct answer for a tenant with no legal documents configured (contract
  // §2), and guarding on emptiness would re-fire the request on every render for
  // exactly those tenants. The claim is also made on failure, so a 5xx does not
  // become a retry loop — it just leaves the entries hidden, silently.
  //
  // It is made on PENDING, not only on settled, so it is visible for the whole round
  // trip rather than only after a response lands.
  //
  // The guard asks whether THIS TOKEN has attempted, not whether the page has. The
  // difference is the whole point: bootstrapLegalDocuments fires for any token in
  // localStorage, so opening /login with an expired one from a previous session left
  // a claim behind that this guard then honoured for the session the user logged
  // into — the sidebar's Terms/Privacy links stayed missing until a reload. Note the
  // sliding renewal (authed.query.ts) also lands a new token, so it costs one extra
  // list request per renewal; that is the correct reading of "a new credential asks
  // once", and the list is small and per-language static.
  //
  // The guard reads `store.getState()` AT CALL TIME and deliberately not a
  // useSelector value. A selector value is captured at the render that produced this
  // effect, so several invocations in one commit batch all observe the pre-fetch
  // `false` and each fire — the integration gate measured exactly three requests on
  // the login path this way. Reading the store directly consults the authoritative
  // current value, and because RTK Query dispatches `pending` synchronously from the
  // trigger, invocation #2 in the same batch already sees the claim.
  //
  // Note the adjacent `/access` call does not need this: it is a QUERY, and
  // RTK Query dedupes identical in-flight queries by cache key. ADR-5 requires this
  // one to be a MUTATION (a cached GET would replay a stale storage url), and
  // mutations never dedupe — so the dedupe has to be explicit here.
  //
  // PD-3030: the list is per language, and the profile page lets the clinician
  // switch language without a new token. So the claim and the trigger are keyed on
  // (token, language): a switch asks once in the new language, and the same pair
  // still never asks twice.
  //
  // useTranslation() is here ONLY for its subscription: react-i18next re-renders
  // this container on `languageChanged`, which is what lets the language in the
  // dependency array below be seen at all. Re-rendering returns the same `children`
  // element, so nothing below re-renders because of it. The value itself is read
  // from the i18next singleton, as before and as the consent gate does
  // (TosAndPP/index.tsx).
  useTranslation();
  const language = i18next.language;
  const store = useStore();
  const [fetchLegalDocuments] = useLegalDocumentsMutation();
  useEffect(() => {
    if (!tokens.access) {
      return;
    }
    // Wire token, not the ISO code — see legalContentLang.
    const lang = legalContentLang(language);
    if (
      selectLegalFetchAttemptedFor(
        store.getState() as LegalOwnerState,
        tokens.access,
        lang
      )
    ) {
      return;
    }
    fetchLegalDocuments({
      lang,
      token: tokens.access,
    });
  }, [tokens.access, language]);

  if (!access) {
    const loginRoute = '/login';
    const isRedirectAttached =
      window.location.search.search(REDIRECT_SEARCH_PARAM_KEY) !== -1;
    const isCurrentInLoginPage = window.location.pathname === loginRoute;

    if (isCurrentInLoginPage) {
      return null;
    }
    let path = loginRoute;
    if (!isRedirectAttached) {
      path += `?${getRedirectQueryStringInCurrentLocation()}`;
    }
    return <Navigate to={path} replace />;
  }

  // Permission gate. The manifest loads asynchronously; until it is present we
  // must neither render a gated page (would flash) nor redirect (would be wrong)
  // — hold. `undefined` = still loading; a loaded manifest may legitimately be
  // empty (e.g. a role with none of the gated capabilities).
  if (requiredPermissions?.length) {
    if (!accessManifest) {
      return null;
    }
    const isRoleCheckPassed = requiredPermissions.every(r =>
      (permissions ?? []).includes(r)
    );
    if (!isRoleCheckPassed) {
      return <Navigate to={'/'} replace />;
    }
  }

  return children;
};

export default AuthedContainer;
